// BUG-005c: per-currency totals for the reports and the data export summary.
// casavello never converts between currencies and never adds euro and pound amounts together: every total is
// worked out for one currency at a time, and each figure is shown with its own currency's symbol.
// Plain CommonJS so the backend Jest suite (the "Tests" check) can test it and the reports can import it.

const { isIrishCompany } = require('./propertyCountry');
const { ownershipMatches } = require('./jointShare');

const CURRENCY_ORDER = ['GBP', 'EUR'];
const SYMBOLS = { GBP: '£', EUR: '€' };

const normCurrency = (c) => {
  const t = String(c === undefined || c === null ? '' : c).trim().toUpperCase();
  return t || 'GBP';
};
const symbolFor = (currency) => SYMBOLS[normCurrency(currency)] || '£';

// "£1,234.00": the same text the reports have always shown, but with the right symbol for the currency
function formatMoney(amount, currency) {
  return `${symbolFor(currency)}${parseFloat(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// GBP first, then EUR (anything else, which cannot be saved any more, sorts after them)
function sortCurrencies(list) {
  return [...new Set(list.map(normCurrency))].sort((a, b) => {
    const ia = CURRENCY_ORDER.indexOf(a); const ib = CURRENCY_ORDER.indexOf(b);
    if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

const num = (v) => parseFloat(v || 0) || 0;
const isPaid = (e) => e.paid === true || e.paid === 'true';
const propId = (x) => x.propertyId || x.property_id;
const money2 = (v) => num(v).toFixed(2);
const pct1 = (v) => `${num(v).toFixed(1)}%`;

// A company overhead takes the currency of its company; one with no company is GBP (the company default).
function companyExpenseCurrency(expense, companies) {
  const id = expense && (expense.company_id || expense.companyId);
  const c = id ? (companies || []).find((co) => String(co.id) === String(id)) : null;
  return normCurrency(c && c.currency);
}

// ---------------------------------------------------------------------------
// Performance and Monthly Profit: twelve months, one block per currency.
// ownership: 'all' | 'personal' | 'company' | 'joint'    currency: 'all' or one currency
function filterProperties(properties, ownership = 'all', currency = 'all') {
  return (properties || [])
    .filter((p) => !p.archived)
    .filter((p) => ownershipMatches(p, ownership))   // BUG-010: 'personal' includes joint properties
    .filter((p) => currency === 'all' || normCurrency(p.currency) === normCurrency(currency));
}

// The payments, expenses and overheads of one year that the on-screen filters let through (used by the Excel detail sheets,
// so a download holds exactly what the screen is showing).
function yearDetail({ properties, payments, expenses, companyExpenses, companies, year, ownership = 'all', currency = 'all' }) {
  const props = filterProperties(properties, ownership, currency);
  const ids = new Set(props.map((p) => String(p.id)));
  const y = String(year);
  const includeCorp = ownership === 'all' || ownership === 'company';
  return {
    props,
    payments: (payments || []).filter((p) => p.month && String(p.month).startsWith(y) && ids.has(String(propId(p)))),
    expenses: (expenses || []).filter((e) => e.date && String(e.date).startsWith(y) && ids.has(String(propId(e)))),
    corp: includeCorp
      ? (companyExpenses || []).filter((e) => e.date && String(e.date).startsWith(y))
        .filter((e) => currency === 'all' || companyExpenseCurrency(e, companies) === normCurrency(currency))
      : [],
  };
}

function monthlyByCurrency({ properties, payments, expenses, companyExpenses, companies, year, ownership = 'all', currency = 'all', monthStyle = 'short' }) {
  const props = filterProperties(properties, ownership, currency);
  const includeCorp = ownership === 'all' || ownership === 'company';
  const yearPrefix = String(year);
  const corpInYear = includeCorp
    ? (companyExpenses || []).filter((e) => isPaid(e) && e.date && String(e.date).startsWith(yearPrefix))
      .filter((e) => currency === 'all' || companyExpenseCurrency(e, companies) === normCurrency(currency))
    : [];
  const currencies = sortCurrencies([...props.map((p) => p.currency), ...corpInYear.map((e) => companyExpenseCurrency(e, companies))]);
  if (!currencies.length) currencies.push(currency === 'all' ? 'GBP' : normCurrency(currency));

  return currencies.map((cur) => {
    const curProps = props.filter((p) => normCurrency(p.currency) === cur);
    const ids = new Set(curProps.map((p) => String(p.id)));
    const months = [];
    for (let m = 1; m <= 12; m++) {
      const monthStr = `${yearPrefix}-${String(m).padStart(2, '0')}`;
      const label = new Date(parseInt(yearPrefix, 10), m - 1, 1).toLocaleDateString('en-GB', { month: monthStyle });
      const inc = (payments || []).filter((p) => p.month === monthStr && ids.has(String(propId(p)))).reduce((s, p) => s + num(p.amount), 0);
      const propExp = (expenses || []).filter((e) => e.date && String(e.date).startsWith(monthStr) && isPaid(e) && ids.has(String(propId(e)))).reduce((s, e) => s + num(e.amount), 0);
      const corpExp = includeCorp
        ? (companyExpenses || []).filter((e) => e.date && String(e.date).startsWith(monthStr) && isPaid(e) && companyExpenseCurrency(e, companies) === cur).reduce((s, e) => s + num(e.amount), 0)
        : 0;
      months.push({ key: monthStr, label, inc, propExp, corpExp, exp: propExp + corpExp, net: inc - propExp - corpExp });
    }
    const sum = (f) => months.reduce((s, m) => s + f(m), 0);
    return {
      currency: cur, symbol: symbolFor(cur), months,
      totals: { inc: sum((m) => m.inc), propExp: sum((m) => m.propExp), corpExp: sum((m) => m.corpExp), exp: sum((m) => m.exp), net: sum((m) => m.net) },
    };
  });
}

// The Excel summary sheet for Performance and Monthly Profit. One block per currency, each with its own annual total.
function monthlySummaryRows({ title, year, today, groups }) {
  const rows = [
    [title, year, '', '', '', '', ''],
    [`Generated: ${today}`, '', '', '', '', '', ''],
    ['Each currency is shown on its own. casavello does not convert between currencies.', '', '', '', '', '', ''],
    [],
    ['Currency', 'Month', 'Income', 'Property Expenses', 'Company Expenses', 'Net Profit', 'Margin %'],
  ];
  groups.forEach((g, i) => {
    if (i > 0) rows.push([]);
    g.months.forEach((m) => rows.push([g.currency, `${m.label} ${year}`, money2(m.inc), money2(m.propExp), money2(m.corpExp), money2(m.net), m.inc > 0 ? pct1((m.net / m.inc) * 100) : '—']));
    rows.push([`ANNUAL TOTAL (${g.currency})`, '', money2(g.totals.inc), money2(g.totals.propExp), money2(g.totals.corpExp), money2(g.totals.net), '']);
  });
  return rows;
}

// One TOTAL row per currency for a detail sheet. entries: [{ currency, amount }]
function totalRowsByCurrency(entries, width, amountIndex, label = 'TOTAL') {
  const totals = {};
  (entries || []).forEach((e) => { const c = normCurrency(e.currency); totals[c] = (totals[c] || 0) + num(e.amount); });
  return sortCurrencies(Object.keys(totals)).map((c) => {
    const row = Array(width).fill('');
    row[0] = `${label} (${c})`;
    row[amountIndex] = money2(totals[c]);
    return row;
  });
}

// ---------------------------------------------------------------------------
// Portfolio Capital
function capitalByCurrency(rows) {
  return sortCurrencies((rows || []).map((p) => p.currency)).map((cur) => {
    const mine = rows.filter((p) => normCurrency(p.currency) === cur);
    const sum = (f) => mine.reduce((s, p) => s + num(f(p)), 0);
    const val = sum((p) => p.currentValue); const mort = sum((p) => p.mortgageBalance);
    return {
      currency: cur, symbol: symbolFor(cur), count: mine.length,
      purchase: sum((p) => p.purchasePrice), val, mort, equity: val - mort, growth: sum((p) => p.capitalGrowth), annualRent: sum((p) => p.annualRent),
      ltv: val > 0 ? (mort / val) * 100 : 0,
    };
  });
}

function capitalRows({ capData, today }) {
  const rows = [
    ['PORTFOLIO CAPITAL REPORT', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    [`Generated: ${today}`, '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    ['Each currency is totalled on its own. casavello does not convert between currencies.', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''],
    [],
    ['Property', 'Type', 'Ownership', 'Currency', 'Purchase Price', 'Current Value', 'Capital Growth', 'Growth %', 'Mortgage Balance', 'Equity', 'LTV %', 'Gross Yield %', 'Annual Rent', 'Provider', 'Rate %', 'Rate Expiry'],
    ...capData.map((p) => [
      p.address, p.property_type || p.propertyType || '', p.ownership || '', normCurrency(p.cur || p.currency),
      p.purchasePrice > 0 ? money2(p.purchasePrice) : '', p.currentValue > 0 ? money2(p.currentValue) : '',
      money2(p.capitalGrowth), pct1(p.capitalGrowthPct),
      p.mortgageBalance > 0 ? money2(p.mortgageBalance) : '', money2(p.equity),
      pct1(p.ltv), pct1(p.grossYield), money2(p.annualRent),
      p.mortgageProvider || '', p.mortgageRate || '', p.rateExpiry || '',
    ]),
    [],
  ];
  capitalByCurrency(capData).forEach((g) => {
    rows.push([`TOTALS (${g.currency})`, '', '', g.currency, money2(g.purchase), money2(g.val), money2(g.growth), '', money2(g.mort), money2(g.equity), pct1(g.ltv), '', money2(g.annualRent), '', '', '']);
  });
  return rows;
}

// ---------------------------------------------------------------------------
// Company Property report. rows: company properties with income, expenses, profit. overheads: the paid overheads of the period.
function companyTotalsByCurrency({ rows, overheads, companies }) {
  const corp = (overheads || []).filter(isPaid);
  const currencies = sortCurrencies([...(rows || []).map((p) => p.currency), ...corp.map((e) => companyExpenseCurrency(e, companies))]);
  if (!currencies.length) currencies.push('GBP');
  return currencies.map((cur) => {
    const mine = (rows || []).filter((p) => normCurrency(p.currency) === cur);
    const inc = mine.reduce((s, p) => s + num(p.income), 0);
    const exp = mine.reduce((s, p) => s + num(p.expenses), 0);
    const overhead = corp.filter((e) => companyExpenseCurrency(e, companies) === cur).reduce((s, e) => s + num(e.amount), 0);
    return { currency: cur, symbol: symbolFor(cur), count: mine.length, inc, exp, profit: inc - exp, overhead, net: inc - exp - overhead };
  });
}

// ---------------------------------------------------------------------------
// Data Export summary text: one block per currency
function exportSummaryByCurrency({ properties, payments, expenses, now = new Date() }) {
  const curOf = new Map((properties || []).map((p) => [String(p.id), normCurrency(p.currency)]));
  const curForProp = (id) => curOf.get(String(id)) || 'GBP';
  const within12m = (d) => { const t = new Date(d); return (now - t) < 365 * 24 * 60 * 60 * 1000; };
  const currencies = sortCurrencies([...curOf.values()]);
  if (!currencies.length) currencies.push('GBP');
  return currencies.map((cur) => {
    const mine = (properties || []).filter((p) => normCurrency(p.currency) === cur);
    const capital = (p) => p.capitalData || p.capital_data || {};
    const monthlyRent = mine.reduce((s, p) => s + (p.rooms || []).filter((r) => r.status === 'occupied').reduce((rs, r) => rs + num(r.rentAmount), 0), 0);
    const value = mine.reduce((s, p) => s + num(capital(p).currentValue || capital(p).purchasePrice), 0);
    const mortgage = mine.reduce((s, p) => s + num(capital(p).mortgageBalance), 0);
    const rentCollected = (payments || []).filter((p) => curForProp(propId(p)) === cur).filter((p) => within12m(p.paidDate || p.paid_date)).reduce((s, p) => s + num(p.amount), 0);
    const propertyExpenses = (expenses || []).filter((e) => curForProp(propId(e)) === cur).filter((e) => within12m(e.date)).reduce((s, e) => s + num(e.amount), 0);
    return { currency: cur, symbol: symbolFor(cur), count: mine.length, monthlyRent, annualRent: monthlyRent * 12, value, mortgage, equity: value - mortgage, rentCollected, propertyExpenses, net: rentCollected - propertyExpenses };
  });
}

// The human-readable lines, in two parts (portfolio, then the last-12-months financials). With one currency they read
// exactly as they always have; with more, each currency has its own block with its currency in every label.
function exportSummaryLines(groups, part) {
  const multi = groups.length > 1;
  const width = multi ? 34 : 25;
  const lines = [];
  groups.forEach((g, i) => {
    const f = (v) => formatMoney(v, g.currency);
    const tag = multi ? ` (${g.currency})` : '';
    const row = (label, value) => `${label}${tag}:`.padEnd(width) + f(value);
    if (multi) { if (i > 0) lines.push(''); lines.push(`── ${g.currency} (${g.symbol}) — ${g.count} propert${g.count === 1 ? 'y' : 'ies'} ──`); }
    if (part === 'portfolio') {
      lines.push(
        row('Monthly Rent Income', g.monthlyRent), row('Annual Rent Income', g.annualRent), '',
        row('Total Portfolio Value', g.value), row('Total Mortgage Balance', g.mortgage), row('Total Equity', g.equity),
      );
    } else {
      lines.push(row('Total Rent Collected', g.rentCollected), row('Total Property Expenses', g.propertyExpenses), row('Net Income', g.net));
    }
  });
  return lines;
}

// ---------------------------------------------------------------------------
// Dashboard and Properties page (BUG-005b). Every total is for one currency; nothing is converted.

// Capital cards: property value, mortgage balance, equity and LTV, one block per currency.
// A property counts when it has a current value or a purchase price (its value is the current value, else the purchase price).
function dashboardCapitalByCurrency(properties) {
  const withData = (properties || []).filter((p) => !p.archived).map((p) => ({ p, cd: p.capitalData || p.capital_data || {} }))
    .filter(({ cd }) => num(cd.currentValue) > 0 || num(cd.purchasePrice) > 0);
  return sortCurrencies(withData.map(({ p }) => p.currency)).map((cur) => {
    const mine = withData.filter(({ p }) => normCurrency(p.currency) === cur);
    const value = mine.reduce((s, { cd }) => s + num(cd.currentValue || cd.purchasePrice), 0);
    const mortgage = mine.reduce((s, { cd }) => s + num(cd.mortgageBalance), 0);
    const purchase = mine.reduce((s, { cd }) => s + num(cd.purchasePrice), 0);
    return { currency: cur, symbol: symbolFor(cur), count: mine.length, value, mortgage, purchase, equity: value - mortgage, ltv: value > 0 ? (mortgage / value) * 100 : 0 };
  });
}

// Vacancy Impact cards. vacantRooms: [{ rentAmount, currency }] (each room carries its property's currency)
function vacancyByCurrency(vacantRooms) {
  return sortCurrencies((vacantRooms || []).map((r) => r.currency)).map((cur) => {
    const mine = vacantRooms.filter((r) => normCurrency(r.currency) === cur);
    const monthlyLost = mine.reduce((s, r) => s + num(r.rentAmount), 0);
    return { currency: cur, symbol: symbolFor(cur), count: mine.length, monthlyLost, annualProjection: monthlyLost * 12 };
  });
}

// Properties page "Monthly Rent" card: occupied-room rent, one figure per currency (GBP 0 when there is nothing)
function monthlyRentByCurrency(properties) {
  const list = properties || [];
  const rentOf = (p) => (p.rooms || []).filter((r) => r.status === 'occupied').reduce((s, r) => s + num(r.rentAmount), 0);
  const currencies = sortCurrencies(list.map((p) => p.currency));
  if (!currencies.length) currencies.push('GBP');
  return currencies.map((cur) => {
    const mine = list.filter((p) => normCurrency(p.currency) === cur);
    return { currency: cur, symbol: symbolFor(cur), count: mine.length, rent: mine.reduce((s, p) => s + rentOf(p), 0) };
  });
}

// Dashboard income and profit for one currency: personal, company and (new) the matching company overheads.
// Company overheads are taken from the overhead's company currency, so euro overheads never reduce a pound profit.
function dashboardIncomeProfit({ properties, payments, expenses, companyExpenses, companies, currency, start, end, paidDateOf }) {
  const cur = normCurrency(currency);
  const inRange = (d) => { if (!d) return false; const t = new Date(d); return t >= start && t <= end; };
  const mine = (properties || []).filter((p) => !p.archived && normCurrency(p.currency) === cur);
  const income = (list) => (payments || []).filter((p) => inRange(paidDateOf(p))).filter((p) => list.some((x) => String(x.id) === String(propId(p)))).reduce((s, p) => s + num(p.amount), 0);
  const spend = (list) => (expenses || []).filter((e) => isPaid(e) && inRange(e.date)).filter((e) => list.some((x) => String(x.id) === String(propId(e)))).reduce((s, e) => s + num(e.amount), 0);
  const overhead = (companyExpenses || []).filter((e) => isPaid(e) && inRange(e.date) && companyExpenseCurrency(e, companies) === cur).reduce((s, e) => s + num(e.amount), 0);
  const personal = mine.filter((p) => p.ownership === 'personal');
  const company = mine.filter((p) => p.ownership === 'company');
  // BUG-010: jointly owned properties get their own lines at 100% (never mixed into the personal figures, no overheads)
  const joint = mine.filter((p) => String(p.ownership || '').toLowerCase() === 'joint');
  const out = {
    currency: cur, symbol: symbolFor(cur), count: mine.length, companyCount: company.length, personalCount: personal.length, jointCount: joint.length,
    personalInc: income(personal), personalExp: spend(personal), companyInc: income(company), companyExp: spend(company), overhead,
    jointInc: income(joint), jointExp: spend(joint),
  };
  out.personalProfit = out.personalInc - out.personalExp;
  out.companyProfit = out.companyInc - out.companyExp - out.overhead;
  out.jointProfit = out.jointInc - out.jointExp;
  return out;
}

// ---------------------------------------------------------------------------
// Company overheads in the UK and Irish tax reports (BUG-005d).
// An overhead belongs to its company: an Irish company (jurisdiction Ireland or currency EUR) reduces only the Irish
// report, in euro; any other company (and an overhead with no company) reduces only the UK report, in pounds.
// An overhead is also only used where its own currency is the report's currency; one that is not (for example a UK
// company set up in euro) is returned in `skipped` so the screen can say it was left out, never silently added.
const REPORT_CURRENCY = { uk: 'GBP', ie: 'EUR' };

function overheadSide(expense, companies) {
  const id = expense && (expense.company_id || expense.companyId);
  const co = id ? (companies || []).find((c) => String(c.id) === String(id)) : null;
  return co && isIrishCompany(co) ? 'ie' : 'uk';
}

// side: 'uk' | 'ie'. start/end: the report's tax-year dates. Only paid overheads dated inside the period count.
function taxReportOverheads({ companyExpenses, companies, side, start, end }) {
  const mine = (companyExpenses || [])
    .filter((e) => isPaid(e) && e.date && new Date(e.date) >= start && new Date(e.date) <= end)
    .filter((e) => overheadSide(e, companies) === side);
  const wanted = REPORT_CURRENCY[side];
  const items = mine.filter((e) => companyExpenseCurrency(e, companies) === wanted);
  const skipped = mine.filter((e) => companyExpenseCurrency(e, companies) !== wanted);
  return { currency: wanted, symbol: symbolFor(wanted), items, total: items.reduce((s, e) => s + num(e.amount), 0), skipped, skippedTotal: skipped.reduce((s, e) => s + num(e.amount), 0) };
}

module.exports = {
  normCurrency, symbolFor, formatMoney, sortCurrencies, companyExpenseCurrency,
  filterProperties, yearDetail, monthlyByCurrency, monthlySummaryRows, totalRowsByCurrency,
  overheadSide, taxReportOverheads,
  dashboardCapitalByCurrency, vacancyByCurrency, monthlyRentByCurrency, dashboardIncomeProfit,
  capitalByCurrency, capitalRows, companyTotalsByCurrency, exportSummaryByCurrency, exportSummaryLines,
};
